import { useEffect, useState } from "react";
import api from "../../lib/api";
import { CheckCheck, Send, Clock, XCircle, MessageCircle } from "lucide-react";

const STATUS_META = {
  delivered: { label: "Delivered", tone: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCheck },
  read: { label: "Read", tone: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCheck },
  sent: { label: "Sent", tone: "bg-blue-50 text-blue-700 border-blue-200", icon: Send },
  queued: { label: "Queued", tone: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  accepted: { label: "Queued", tone: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock },
  failed: { label: "Failed", tone: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
  undelivered: { label: "Undelivered", tone: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
  api_rejected: { label: "Rejected", tone: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
  mock_skipped: { label: "Skipped (mock)", tone: "bg-slate-100 text-slate-600 border-slate-200", icon: Clock },
  exception: { label: "Error", tone: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
};

/** Delivery-status tracker: one row per outbound WhatsApp send, showing the
 * Activity/context it was sent against, timestamp, and real Twilio delivery status.
 * Reusable from both the Lead detail (tab) and Opportunity detail (modal). */
export default function WhatsAppTracker({ leadId, oppId }) {
  const [msgs, setMsgs] = useState(null);

  useEffect(() => {
    if (!leadId && !oppId) { setMsgs([]); return; }
    setMsgs(null);
    const params = new URLSearchParams();
    if (leadId) params.set("lead_id", leadId);
    if (oppId) params.set("opp_id", oppId);
    api.get(`/whatsapp/messages?${params.toString()}`)
      .then(({ data }) => setMsgs(data))
      .catch(() => setMsgs([]));
  }, [leadId, oppId]);

  if (msgs === null) {
    return <div className="text-xs font-mono text-[#94A3B8] py-8 text-center" data-testid="whatsapp-tracker-loading">Loading…</div>;
  }
  if (msgs.length === 0) {
    return (
      <div className="text-xs font-mono text-[#94A3B8] py-8 text-center flex flex-col items-center gap-2" data-testid="whatsapp-tracker-empty">
        <MessageCircle className="w-5 h-5 opacity-40" /> No WhatsApp messages sent yet.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="whatsapp-tracker-list">
      {msgs.map((m) => {
        const meta = STATUS_META[m.status] || { label: m.status, tone: "bg-slate-100 text-slate-600 border-slate-200", icon: Clock };
        const Icon = meta.icon;
        return (
          <div key={m.id} className="border border-[#E2E4DC] p-3 flex items-start gap-3" data-testid={`whatsapp-msg-${m.id}`}>
            <div className={"inline-flex items-center gap-1 px-2 py-1 border text-[10px] font-mono uppercase tracking-widest flex-shrink-0 " + meta.tone} data-testid={`whatsapp-msg-status-${m.id}`}>
              <Icon className="w-3 h-3" /> {meta.label}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-[#0F1A12] truncate" data-testid={`whatsapp-msg-activity-${m.id}`}>
                {m.context || "WhatsApp notification"}
              </div>
              <div className="text-[11px] font-mono text-[#475569] mt-0.5 truncate">{(m.body || "").split("\n")[0]}</div>
              {m.error_message && (
                <div className="text-[10.5px] font-mono text-red-600 mt-1" data-testid={`whatsapp-msg-error-${m.id}`}>{m.error_message}</div>
              )}
            </div>
            <div className="text-[10px] font-mono text-[#94A3B8] flex-shrink-0 text-right whitespace-nowrap" data-testid={`whatsapp-msg-time-${m.id}`}>
              {new Date(m.updated_at || m.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
